import React, { useEffect, useState, useRef } from "react";
import {
  DownArrow,
  LeftArrow,
  LeftArrowWithTail,
  Menu,
  RightArrow,
  Search,
} from "../../assets/icons/Icons";
import { FaPause, FaPlay } from "react-icons/fa";
import { DocProfileData } from "../../services/types";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import ProfilePicture from "../atoms/ProfilePicture";
import Toggle from "react-toggle";
import moment from "moment";
import SwitchBranch from "./modals/SwitchBranch";
import { modalStyles, switchBranch } from "../../assets/styles/modalStyles";
import MobileSidebar from "./MobileSidebar";
import { useHospDocData } from "../../lib/contexts/HospitalDoctorContext";
import ClinicModal from "./modals/HospModal";
import OverflowString from "../atoms/OverflowString";
import {
  getCheckInDoctor,
  pauseDoctorSession,
  setCheckInDoctor,
} from "../../lib/apis/doctor";
import { isFirebaseStorageUrl } from "../../lib/utils/funcs";
import { DataForCheckdin } from "../../lib/utils/types";
import NotificationBell from "./notifications/NotificationBell";
import Notifications from "./notifications/Notifications";
import { getUnreadNotificationsCount } from "../../lib/apis/notifications";
import { useUserData } from "../../lib/contexts/UserContext";
import { useFeatureFlags } from "../../lib/contexts/FeatureFlagContext";
import {
  playNotificationSound,
  setupAudioInteraction,
} from "../../lib/utils/audioUtils";
import { toast } from "react-toastify";

const DocDetailsHeader = ({
  docData,
  data,
}: {
  docData?: DocProfileData;
  data?: DataForCheckdin;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { allHospData, hospitalID, doctorsData, hospData } = useHospDocData();
  const params = useParams();
  const mapping_id = params.mapping_id;
  const { userData } = useUserData();
  const [switchVisible, setSwitchVisible] = useState(false);
  const [clinicVisible, setClinicVisible] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);
  const [available, setAvailable] = useState(false);
  const [hoveredDoctorId, setHoveredDoctorId] = useState("");
  const [isNotificationsOpen, setNotificationsOpen] = useState(false);
  const [addtionalCheckIndata, setAdditionalCheckInData] = useState({
    is_paused: false,
    pause_reason: "",
  });
  const [checkIndata, setCheckInData] = useState({
    mapping_id: "",
    date: "",
    session: "",
  });
  const [search, setSearch] = useState("");
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [notificationCount, setNotificationCount] = useState(0);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState<number | null>(null);
  const prevNotificationCountRef = useRef(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const { notification, check_in_pause } = useFeatureFlags();

  // Filter to only show active doctors
  const activeDoctors = (doctorsData ?? []).filter((doc) => doc.is_active);

  const searchContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        setIsSearchExpanded(false); // Close the bar
        setSearch("");              // Clear the text
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // 🔹 Always array, never undefined
  const filteredAndSearchedDoctors =
    search !== ""
      ? activeDoctors.filter((doc) =>
        doc.full_name.toLowerCase().includes(search.toLowerCase())
      )
      : [];

  // Track user interaction to enable audio playback
  useEffect(() => {
    const handleUserInteraction = () => {
      setHasUserInteracted(true);
      document.removeEventListener("click", handleUserInteraction);
      document.removeEventListener("touchstart", handleUserInteraction);
      document.removeEventListener("keydown", handleUserInteraction);
    };

    document.addEventListener("click", handleUserInteraction);
    document.addEventListener("touchstart", handleUserInteraction);
    document.addEventListener("keydown", handleUserInteraction);

    return () => {
      document.removeEventListener("click", handleUserInteraction);
      document.removeEventListener("touchstart", handleUserInteraction);
      document.removeEventListener("keydown", handleUserInteraction);
    };
  }, []);

  const docAvail = async (data: DataForCheckdin) => {
    const res = await getCheckInDoctor(
      data.mapping_id,
      data.date,
      data.session
    );
    if (res?.status === 200) {
      setAvailable(res.data.result.doc_in);
      setAdditionalCheckInData({
        is_paused: res.data.result.is_paused,
        pause_reason: res.data.result.pause_reason,
      });
    } else {
      setAvailable(false);
    }
  };

  const fetchUnreadCount = async () => {
    if (userData?.role !== "Doctor") {
      const count = await getUnreadNotificationsCount(userData?.user_id, false);
      const storedCount =
        localStorage.getItem(`notification_count_${userData?.user_id}`) || "0";
      const prevCount = parseInt(storedCount, 10);

      const isMuted = localStorage.getItem("notifications_muted") === "true";

      if (
        count > prevCount &&
        prevCount >= 0 &&
        hasUserInteracted &&
        count !== prevCount &&
        !isMuted
      ) {
        try {
          if (audioRef.current) {
            audioRef.current.currentTime = 0;
            await audioRef.current.play();
          }
        } catch (error) {
          console.log("Audio playback failed:", error);
        }
      }

      localStorage.setItem(
        `notification_count_${userData?.user_id}`,
        count.toString()
      );
      setNotificationCount(count);
    }
  };  

  useEffect(() => {
    if (
      notificationCount > prevNotificationCountRef.current &&
      prevNotificationCountRef.current > 0 &&
      notification
    ) {
      playNotificationSound(audioRef.current);
    }
    prevNotificationCountRef.current = notificationCount;
  }, [notificationCount]);

  useEffect(() => {
    return setupAudioInteraction();
  }, []);

  useEffect(() => {
    const getDocAvail = async (data: DataForCheckdin) => {
      const res = await getCheckInDoctor(
        data.mapping_id,
        data.date,
        data.session
      );
      if (res?.status === 200) {
        setAvailable(res.data.result.doc_in);
        setAdditionalCheckInData({
          is_paused: res.data.result.is_paused,
          pause_reason: res.data.result.pause_reason,
        });
      } else {
        setAvailable(false);
      }
    };
    if (data && data.mapping_id && data.date && data.session) {
      setCheckInData(data);
      getDocAvail(data);
    }
  }, [data]);
  

  const tabsBox: HTMLElement | null = document.querySelector(".tabs-box2");
  if (tabsBox !== null) {
    const arrowIcons: NodeListOf<Element> =
      document.querySelectorAll(".icon svg");

    let isDragging: boolean = false;

    const handleIcons = (scrollVal: number): void => {
      let maxScrollableWidth: number =
        tabsBox.scrollWidth - tabsBox.clientWidth;

      (arrowIcons[0].parentElement as HTMLElement).style.display =
        scrollVal <= 0 ? "none" : "flex";
      (arrowIcons[1].parentElement as HTMLElement).style.display =
        maxScrollableWidth - scrollVal <= 1 ? "none" : "flex";
    };

    arrowIcons.forEach((icon: Element) => {
      icon.addEventListener("click", () => {
        let scrollWidth: number = (tabsBox.scrollLeft +=
          icon.id === "left" ? -101 : 101);

        handleIcons(scrollWidth);
      });
    });

    const dragging = (e: MouseEvent): void => {
      if (!isDragging) return;
      tabsBox.classList.add("dragging");
      tabsBox.scrollLeft -= e.movementX;
      handleIcons(tabsBox.scrollLeft);
    };

    const dragStop = (): void => {
      isDragging = false;
      tabsBox.classList.remove("dragging");
    };

    tabsBox.addEventListener("mousedown", () => (isDragging = true));
    tabsBox.addEventListener("mousemove", dragging);
    document.addEventListener("mouseup", dragStop);
  }

  return (
    <>
      <audio
        ref={audioRef}
        src="https://ik.imagekit.io/gn9by35ed/new-notification-09-352705.mp3?updatedAt=1753245591802"
        preload="auto"
        style={{ display: "none" }}
      />
      <div className="bg-white h-[64px] px-3 lg:pl-7 lg:pr-11 lg:py-5 flex justify-between items-center border-solid border-b-[1px] border-sbBorder w-full">
        <div className="flex flex-row items-center">
          <button
            className="flex xl:hidden mr-3"
            onClick={() => setMenuVisible(true)}
          >
            <Menu />
          </button>
          <button
            className="hidden lg:flex mr-5"
            onClick={() => navigate("/" + hospitalID + "/doctors")}
          >
            <LeftArrowWithTail />
          </button>
          {docData?.profile_picture === docData?.full_name ||
            !isFirebaseStorageUrl(docData?.profile_picture) ? (
            <ProfilePicture
              username={docData?.full_name ? docData?.full_name : ""}
              className={"w-10 h-10  mx-2 "}
              is_active={
                doctorsData &&
                doctorsData.find((doc) => doc.mapping_id === mapping_id)
                  ?.is_active
              }
            />
          ) : (
            <div className="relative w-[35px] h-[35px] hidden md:flex">
              <img
                className="w-full h-full object-contain rounded-full"
                src={docData?.profile_picture}
                alt="doctor"
              />
              <span
                className={`
             absolute bottom-0 right-0
             block
             w-3.5 h-3.5
             rounded-full
             border-2 border-white
             ${docData?.is_active ? "bg-green-500" : "bg-gray-400"}
           `}
              />
            </div>
          )}
          <div className="flex flex-col ml-2">
            <div className="flex flex-row items-center">
              <p className="font-semibold text-sm md:text-lg leading-5 ">
                {docData?.full_name ? `Dr. ${docData.full_name}` : "Loading..."}
              </p>
            </div>
            <p
              className="text-[9px] md:text-xs text-docDetail truncate max-w-[120px] md:max-w-[180px] lg:max-w-[250px]"
              title={`${docData?.qualification}, ${docData?.specialization}`}
            >
              {docData?.qualification}, {docData?.specialization}
            </p>
          </div>
          {data &&
            data.date === moment().format("YYYY-MM-DD") &&
            data.session !== "undefined" && (
              <div className="flex gap-3">
                <div className="flex flex-col ml-2 md:ml-5">
                  <p className="text-[9px] md:text-xs text-docDetail">
                    Check-In
                  </p>
                  <div className="transform scale-75 md:scale-100 origin-left">
                    <Toggle
                      icons={false}
                      defaultChecked={available}
                      checked={available}
                      onChange={async () => {
                        setAvailable(true);
                        const res = await setCheckInDoctor(
                          checkIndata.mapping_id,
                          checkIndata.date,
                          checkIndata.session,
                          true
                        );
                        if (res?.status === 200){
                          await docAvail(checkIndata);
                        }
                      }}
                      disabled={available}
                    />
                  </div>
                </div>
                {/* Pause/Resume Button with Icon and Hover Text */}
                {available && check_in_pause && (
                  <div className="relative group">
                    <button
                     onClick={async () => {
                      if (addtionalCheckIndata.is_paused) {
                        const res = await pauseDoctorSession(
                          checkIndata.mapping_id,
                          checkIndata.session,
                          checkIndata.date,
                          !addtionalCheckIndata.is_paused,
                          ""
                        );
                        if (res?.status === 200) await docAvail(checkIndata); // 🔹 Refresh
                        toast.success("Session Resumed");
                      } else {
                        setShowPauseModal(true);
                      }
                    }}                    
                      className={`flex items-center justify-center mt-1.5 md:mt-2  p-2 mr-2 rounded-full w-[28px] h-[28px] md:w-[35px] md:h-[35px] transition-colors duration-200 
      ${!addtionalCheckIndata.is_paused
                          ? "bg-red-600 hover:bg-red-700"
                          : "bg-[#3F63CB] hover:bg-[#3F63CB]"
                        }`}
                    >
                      {!addtionalCheckIndata.is_paused ? (
                        <FaPause className="text-white text-sm md:text-lg" />
                      ) : (
                        <FaPlay className="text-white text-sm md:text-lg relative   left-0.5" />
                      )}
                    </button>

                    {/* Tooltip text */}
                    <span className="absolute left-1/2 -translate-x-1/2 top-14 w-max bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                      {addtionalCheckIndata.is_paused
                        ? "Resume Session"
                        : "Pause Session"}
                    </span>
                  </div>
                )}
              </div>
            )}
          {showPauseModal && (
            <div className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm z-50">
              <div className="bg-white rounded-2xl shadow-xl w-80 p-6 animate-fadeIn">
                {/* Header */}
                <h2 className="text-lg font-semibold text-gray-800 text-center mb-4">
                  Select Pause Duration
                </h2>

                {/* Options */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                  {[5, 10, 15, 20, 30].map((min) => (
                    <button
                      key={min}
                      onClick={() => setSelectedDuration(min)}
                      className={`py-2 text-sm font-medium rounded-lg border transition-all duration-150
              ${selectedDuration === min
                          ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                          : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
                        }`}
                    >
                      {min} min
                    </button>
                  ))}
                </div>

                {/* Buttons */}
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => {
                      setShowPauseModal(false);
                      setSelectedDuration(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                  >
                    Cancel
                  </button>
                  <button
                    disabled={!selectedDuration}
                    onClick={async () => {
                      const reason = `The doctor is attending an emergency. The queue is paused and will resume in approximately ${selectedDuration} minutes. Thank you for your patience.`;

                      const res = await pauseDoctorSession(
                        checkIndata.mapping_id,
                        checkIndata.session,
                        checkIndata.date,
                        true,
                        reason
                      );

                      if (res?.status === 200) {
                        await docAvail(checkIndata); 
                        toast.warn("Session Paused");
                      }

                      setShowPauseModal(false);
                      setSelectedDuration(null);
                    }}
                    className={`px-4 py-2 text-sm font-medium rounded-lg text-white transition 
            ${selectedDuration
                        ? "bg-blue-600 hover:bg-blue-700"
                        : "bg-blue-300 cursor-not-allowed"
                      }`}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="flex lg:gap-3 xl:gap-3">
          <div className="hidden md:flex">
            <div className="wrapper px-6 md:w-56 lg:w-60 text-queueText text-sm font-medium z-20">
              {activeDoctors && activeDoctors.length > 3 && (
                <div className="icon cursor-pointer flex">
                  <LeftArrow />
                </div>
              )}

              <div
                className="tabs-box2 space-x-2 overflow-y-hidden md:pl-2 lg:pl-4"
                style={{ gap: "5px" }}
              >
                {activeDoctors
                  ?.filter((doctor) => doctor.mapping_id !== mapping_id)
                  .map((doctor) => (
                    <button
                      key={doctor.doctor_id}
                      onMouseEnter={() => setHoveredDoctorId(doctor.mapping_id)}
                      onMouseLeave={() => setHoveredDoctorId("")}
                      onClick={() =>
                        navigate(
                          location.pathname.includes("/doctors/doc/")
                            ? `/${hospitalID}/doctors/doc/${doctor.mapping_id}`
                            : `/${hospitalID}/doctors/${doctor.mapping_id}`
                        )
                      }
                      className="flex flex-col items-center pt-2"
                      style={{ margin: 0 }}
                    >
                      {doctor?.profile_picture?.startsWith("http") ? (
                        <div className="headCard w-12">
                          <div className="headCard-info flex justify-center">
                            <div className="relative w-10 h-10">
                              <img
                                className="w-full h-full object-contain rounded-full"
                                src={doctor.profile_picture}
                                alt="doctor"
                              />
                            </div>
                          </div>
                          <ul className="headCard-social">
                            <li className="headCard-social__item">
                              <div className="headCard-subtitle relative bottom-5 text-sm">
                                {doctor.full_name.split(" ")[0]}
                              </div>
                            </li>
                          </ul>
                        </div>
                      ) : (
                        <div className="headCard w-[3.5rem]">
                          <div className="headCard-info flex items-center justify-center">
                            <ProfilePicture
                              username={doctor?.full_name || ""}
                              is_active={null}
                              className={`${activeDoctors.length < 10 ? "mx-0" : "mx-1"
                                } w-9 h-9`}
                            />
                          </div>
                          <ul className="headCard-social">
                            <li className="headCard-social__item">
                              <div className="headCard-subtitle relative bottom-5 text-sm">
                                {doctor.full_name.split(" ")[0]}
                              </div>
                            </li>
                          </ul>
                        </div>
                      )}
                    </button>
                  ))}
              </div>

              {activeDoctors && activeDoctors.length > 3 && (
                <div className="icon cursor-pointer flex">
                  <RightArrow />
                </div>
              )}
            </div>

              <div 
              ref={searchContainerRef} 
              className="flex-col justify-center hidden md:flex relative ml-10"
            >
                <div 
                className={`flex items-center rounded-full ml-4 h-10 transition-all duration-300 ease-in-out ${
                  isSearchExpanded 
                    ? "bg-white border-[0.5px] border-sbBorder pr-2 shadow-sm" 
                    : "bg-transparent border-transparent"
                }`}
              >
                <input
                  ref={searchInputRef}
                  className={`transition-all duration-300 ease-in-out bg-transparent border-0 rounded-l-full focus:outline-none text-sm ${
                    isSearchExpanded
                      ? "w-20 md:w-40 lg:w-40 xl:w-60 px-4 opacity-100"
                      : "w-0 px-0 opacity-0"
                  }`}
                  type="text"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <button 
                  onClick={() => {
                    setIsSearchExpanded(!isSearchExpanded);
                    if (!isSearchExpanded) {
                      // Slight delay to allow transition to start before focusing
                      setTimeout(() => {
                        searchInputRef.current?.focus();
                      }, 100);
                    }
                  }}
                  className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
                  title="Search Doctor"
                >
                  <Search />
                </button>
              </div>
              {search !== "" && (
                <div className="p-4 px-0 rounded-lg bg-white absolute shadow-lg top-12 left-4 md:w-32 lg:w-52 xl:w-72 z-20">
                  {/* ... (The logic inside this dropdown remains exactly the same as your original code) ... */}
                  {filteredAndSearchedDoctors.length === 0 && (
                    <div className="flex gap-4 items-center justify-center">
                      <div>
                        <p className="text-md">No doctor found</p>
                      </div>
                    </div>
                  )}
                  {filteredAndSearchedDoctors.length > 0 && (
                    <ul className="flex flex-col gap-2 max-h-[300px] overflow-y-auto">
                      {filteredAndSearchedDoctors.map((doctor) => (
                        <li
                          key={doctor.mapping_id}
                          className="cursor-pointer flex items-center gap-3 px-3 py-2 hover:bg-gray-100 rounded-md"
                          onClick={() => {
                            navigate(
                              location.pathname.includes("/doctors/doc/")
                              ? `/${hospitalID}/doctors/doc/${doctor.mapping_id}`
                              : `/${hospitalID}/doctors/${doctor.mapping_id}`
                            );
                            setIsSearchExpanded(false); // Manually close the search bar
                            setSearch("");              // Clear the text
                          }}
                        >
                          {doctor?.profile_picture?.startsWith("http") ? (
                            <img
                              src={doctor.profile_picture}
                              alt={doctor.full_name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <ProfilePicture
                              username={doctor?.full_name || ""}
                              className="w-10 h-10"
                              is_active={doctor.is_active}
                            />
                          )}
                          <div className="flex flex-col">
                            <span className="font-medium text-sm">
                              {`Dr.${doctor.full_name}`}
                            </span>
                            <span className="text-xs text-gray-500">
                              {doctor.specialization}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-row items-center">
            {notification && 
             userData?.role !== "Doctor" && 
             !location.pathname.includes("/doctors/doc/") && (
              <div className="mr-0 md:mr-4">
                <NotificationBell
                  newNotifications={notificationCount}
                  onClick={() => setNotificationsOpen(true)}
                />
              </div>
            )}

            <button
              onClick={() => {
                if (allHospData) {
                  if (allHospData.length > 1) {
                    setSwitchVisible(true);
                    setClinicVisible(false);
                  } else if (hospData !== undefined) {
                    setSwitchVisible(false);
                    setClinicVisible(true);
                  } else {
                    setClinicVisible(false);
                    setSwitchVisible(false);
                  }
                }
              }}
              className="flex flex-row items-center ml-0 md:ml-4"
            >
              {hospData?.logo === "test" || hospData?.logo === "" ? (
                <ProfilePicture
                  username={hospData.hospital_name}
                  is_active={null}
                />
              ) : (
                <img
                  key={hospData?.logo}
                  className="w-8 md:w-12 h-8 md:h-12 rounded-full object-contain"
                  src={hospData?.logo}
                  alt="hospital-logo"
                />
              )}
              <p className="font-medium text-sm ml-2 lg:ml-2 mr-3 hidden xl:flex">
                {hospData ? hospData.hospital_name : ""}
              </p>
              {allHospData && allHospData.length > 1 && <DownArrow />}
            </button>
          </div>
        </div>
        <SwitchBranch
          modalIsOpen={switchVisible}
          closeModal={() => setSwitchVisible(false)}
          customStyles={switchBranch}
        />
        <ClinicModal
          modalIsOpen={clinicVisible}
          closeModal={() => setClinicVisible(false)}
          customStyles={modalStyles}
        />
        {menuVisible && (
          <MobileSidebar visible={menuVisible} setVisible={setMenuVisible} />
        )}
      </div>
      {notification && (
        <Notifications
          isOpen={isNotificationsOpen}
          onClose={() => setNotificationsOpen(false)}
          userId={userData?.user_id}
          onStateChange={fetchUnreadCount}
        />
      )}
    </>
  );
};

export default DocDetailsHeader;
